/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Directive, EventEmitter, Inject, Input, Output } from '@angular/core';

import {
  createEmptySlicedDataSource,
  SlicedData,
  SlicedDataQuery,
  SlicedDataSource,
  SortDirection,
} from '@gh/core-data';
import { UI_ERROR_HANDLER, UiErrorHandler } from '@gh/core-ui';
import { isNotNil, logError, setFields } from '@gh/core-util';
import { combineLatest$ } from '@gh/rx';
import { debounceTime$, distinctUntilChanged$, map$, switchMap$, tap$ } from '@gh/rx/operators';

import { BodyScrollEvent, ColDef, GridApi, GridOptions } from 'ag-grid-community';

import { getDisplayedRowIndexNearScrollPixel, setGridRowData } from '../../grid-api.utils';
import { GRID_EVENT } from '../../grid-public-events';
import invariant from 'invariant';
import { find, get, isEqual, omit } from 'lodash';
import { BehaviorSubject, Subscription } from 'rxjs';
import { animateGridRow } from '../../grid-internal.utils';

import { GridDefinitionManager } from '../../layout/grid-definition-manager.service';
import {
  GridPaginationCursor,
  GridPaginationCursorState,
  newGridPaginationCursor,
} from '../../state';
import {
  GRID_FEATURE,
  GridFeature,
  GridFeatureInitParams,
  GridFeatureReadyParams,
} from '../feature.interfaces';
import { PaginationApi } from './pagination-api';

/**
 * Returns only part of {@link GridPaginationCursorState} which is meaningful for querying of data
 * @param cursor
 */
const normalizeCursorQueryState = (state: GridPaginationCursorState): GridPaginationCursorState =>
  omit(state, 'scroll', 'focusedId');
@Directive({
  standalone: false,
  selector: '[ghGridPagination]',
  providers: [{
    provide: GRID_FEATURE,
    useExisting: GridPaginationDirective,
    multi: true,
  }],
  exportAs: 'ghGridPagination',
})
export class GridPaginationDirective implements GridFeature, PaginationApi {
  updated$ = new EventEmitter<PaginationApi>();

  @Input() sorting = true;

  @Output() modelUpdated = new EventEmitter<void>();
  @Output() beforeModelUpdated = new EventEmitter<void>();
  @Output() onDataIsLoaded = new EventEmitter<SlicedData<any>>();

  private api: GridApi;

  private _cursor = newGridPaginationCursor();
  private dataSource$$ = new BehaviorSubject<SlicedDataSource<any>>(createEmptySlicedDataSource());
  private subscription: Subscription;
  private initialized = false;

  private externalCursor = false;
  private _pageCount: number = -1;
  private _total: number = -1;
  // holds the position to scroll to
  private nextScroll: number = 0;
  // holds last focused id, if id in state differs from this one => corresponding row will be highlighted
  private lastFocusedId?: any;
  private gridElement: HTMLElement;

  @Input()
  set cursor(cursor: GridPaginationCursor) {
    this._cursor = cursor;
    this.externalCursor = true;
  }

  @Input()
  set dataSource(dataSource: SlicedDataSource<any>) {
    // we reset counters only when dataSource is set after initialization,
    // in the time of initialization counters have either default values or values retrieved from external cursor.
    if (this.initialized) {
      this.resetCounters();
    }

    this.nextScroll = 0;
    if (dataSource) {
      this.dataSource$$.next(dataSource);
    }
  }

  @Input()
  set pageSize(n: number) {
    if (this.pageSize === n) {
      return;
    }
    if (this._total >= 0) {
      this._pageCount = this.calculatePageCount();
    }
    this.definitionManager.pageSize = n;
    this.nextScroll = 0;
    this.mergeCursorState({
      page: Math.min(Math.floor(this.pageSize * this.currentPage / n), this._pageCount),
      pageSize: n,
    });
  }

  get pageSize(): number {
    const cursorPageSize = this._cursor.state.pageSize;
    return cursorPageSize < 0 ? this.definitionManager.pageSize : cursorPageSize;
  }

  @Input()
  set currentPage(n: number) {
    let newCurrentPage = n;
    if (this._total >= 0) {
      newCurrentPage = Math.min(n, this._pageCount - 1);
    }

    if (this.currentPage !== newCurrentPage) {
      this.nextScroll = 0;
      this.mergeCursorState({
        page: newCurrentPage,
      });
    }
  }

  get currentPage(): number {
    return this._cursor.state.page;
  }

  get total(): number {
    return this._total;
  }

  get pageCount(): number {
    return this._pageCount;
  }

  constructor(private definitionManager: GridDefinitionManager,
              @Inject(UI_ERROR_HANDLER) private uiErrorHandler: UiErrorHandler) {
  }

  onGridReady(params: GridFeatureReadyParams): void {
    this.initialized = true;
    this.nextScroll = this._cursor.state.scroll || 0;
    this.api = params.api;
    this.api.addEventListener(GRID_EVENT.sortChanged, this.onSortUpdated.bind(this));
    this.api.addEventListener(GRID_EVENT.modelUpdated, this.onGridModelUpdated.bind(this));
    this.api.addEventListener(GRID_EVENT.bodyScroll, this.onGridBodyScroll.bind(this));
    this.startListening();
  }

  initGrid(params: GridFeatureInitParams): void {
    this.gridElement = params.nativeElement;
  }

  enhanceGridOptions(options: GridOptions): void {
    options.defaultColDef = {
      ...options.defaultColDef,
      sortable: isNotNil(this.sorting) ? this.sorting : get(options.defaultColDef, 'sortable', true),
    };

    const cursorSort = this._cursor ? this._cursor.state.sort : void 0;
    if (cursorSort) {
      const columnDefs = <ColDef[]>options.columnDefs || [];
      const columnSorted = columnDefs.filter((column) => column.sort);
      const columnToSort = find(columnDefs, { field: cursorSort.field });
      if (columnToSort) {
        columnSorted.forEach((column) => {
          column.sort = void 0;
        });
        columnToSort.sort = cursorSort.direction === SortDirection.Descending ? 'desc' : 'asc';
      }
    }
  }

  onDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  nextPage(): number {
    let newCurrentPage = this.currentPage;
    if (this._total >= 0) {
      newCurrentPage = Math.min(this.currentPage + 1, this._pageCount - 1);
    } else {
      newCurrentPage++;
    }
    if (this.currentPage !== newCurrentPage) {
      this.nextScroll = 0;
      this.mergeCursorState({
        page: newCurrentPage,
      });
    }
    return this.currentPage;
  }

  previousPage(): number {
    let newCurrentPage = this.currentPage;
    if (this._total >= 0) {
      newCurrentPage = Math.max(this.currentPage - 1, 0);
    } else {
      newCurrentPage--;
    }

    if (this.currentPage !== newCurrentPage) {
      this.nextScroll = 0;
      this.mergeCursorState({
        page: newCurrentPage,
      });
    }

    return this.currentPage;
  }

  onGridModelUpdated(): void {
    this.modelUpdated.emit();
  }

  onBeforeGridModelUpdated(): void {
    this.beforeModelUpdated.emit();
  }

  private setResult(result: SlicedData<any>): void {
    setGridRowData(this.api, result.data);

    this._total = result.total;
    const newPageCount = this.calculatePageCount();
    const newCurrentPage = Math.min(this.currentPage, Math.max(0, newPageCount - 1));

    if (this.currentPage !== newCurrentPage) {
      this.mergeCursorState({
        page: newCurrentPage,
      });
    }
    if (this._pageCount !== newPageCount) {
      this._pageCount = newPageCount;

      // this.paginationChanged();
    }
  }

  /**
   * Builds query for {@link SlicedDataSource}
   *
   * @returns {SlicedDataQuery}
   */
  private buildQuery(): SlicedDataQuery {
    const { pageSize } = this;
    const from = this.currentPage * pageSize;
    const to = from + pageSize;
    const { sort } = this._cursor.state;
    return {
      from,
      to: Math.max(0, to),
      sorting: sort,
    };
  }

  private onGridBodyScroll(event: BodyScrollEvent): void {
    this.mergeCursorState({
      scroll: event.top,
    });
  }

  private onSortUpdated(): void {
    // currently we support sorting only by one column
    const sorted = this.api.getColumnState()
      .filter((c) => c.sort != null)
      .sort((a, b) => (a.sortIndex ?? 0) - (b.sortIndex ?? 0));
    const sortDef = sorted[0];
    if (sortDef?.sort) {
      const column = this.api.getColumnDef(sortDef.colId);

      invariant(column?.field, 'Field name is not defined for column');

      this.mergeCursorState({
        sort: {
          field: <string>column!.field,
          direction: sortDef.sort === 'desc' ? SortDirection.Descending : SortDirection.Ascending,
        },
        page: 0,
      });
    } else {
      this.mergeCursorState({
        sort: void 0,
        page: 0,
      });
    }
  }

  private startListening(): void {
    // search is fired as soon as data source and request are in the stream
    this.subscription = combineLatest$(
      this.dataSource$$,
      this._cursor.changes.pipe(
        distinctUntilChanged$((a, b) => isEqual(normalizeCursorQueryState(a), normalizeCursorQueryState(b))),
        map$(() => this.buildQuery())),
    ).pipe(
      tap$(() => this.api.setGridOption('loading', true)),
      tap$(this.onBeforeGridModelUpdated.bind(this)),
      debounceTime$(300),
      switchMap$(([dataSource, query]) => dataSource.select(query)))
      .subscribe(
        (result) => {
          this.api.setGridOption('loading', false);
          if (this.onDataIsLoaded) {
            this.onDataIsLoaded.emit(result);
          }
          try {
            this.setResult(result);
            // set scroll to the position from cursor
            if (this.nextScroll > 0) {
              const rowIndex = getDisplayedRowIndexNearScrollPixel(this.api, this.nextScroll);
              if (rowIndex != null && rowIndex >= 0) {
                this.api.ensureIndexVisible(rowIndex, 'top');
              }
            }
            // flash focused row
            const { focusedId } = this._cursor.state;
            if (this.lastFocusedId !== focusedId) {
              const row = focusedId != null ? this.api.getRowNode(String(focusedId)) : undefined;
              if (row && row.rowIndex != null) {
                this.api.ensureIndexVisible(row.rowIndex);
                animateGridRow(this.gridElement, row, 'cell-focused', 2000);
              }
              this.lastFocusedId = this._cursor.state.focusedId;
            }
          } catch (error) {
            logError(error);
          }
          this.updated$.emit(this);
        },
        (error) => {
          this.api.setGridOption('loading', false);
          this.uiErrorHandler.raise(error);
        },
      );
  }

  private calculatePageCount(): number {
    return Math.ceil(this._total / this.pageSize);
  }

  private resetCounters(): void {
    this._pageCount = -1;
    this._total = -1;

    this.mergeCursorState({
      page: 0,
    });
  }

  private mergeCursorState(state: Partial<GridPaginationCursorState>): void {
    this._cursor.setState(setFields(this._cursor.state, state));
  }
}
